import { mat4, ReadonlyVec2, ReadonlyVec3, vec3 } from 'gl-matrix';
import { RegistrationUndoService } from '~/domain/undo/RegistrationUndoService';
import { Environment } from '~/engine/data/Environment';
import { TextureView } from '~/engine/view/TextureView';
import { PlaneView } from '~/engine/view/views/PlaneView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';
import { ModifierKeys } from '../../ModifierKeys';
import { ToolAction } from '../../ToolAction';

/** Translation step size for keyboard micro-adjustments in mm */
const TRANSLATION_STEP_MM = 0.5;

class TranslationActionEventState {
    public readonly startPatient: ReadonlyVec3;
    public readonly startMatrix: mat4;
    public readonly registrationId: string;

    constructor(
        startPatient: ReadonlyVec3,
        startMatrix: mat4,
        registrationId: string
    ) {
        this.startPatient = startPatient;
        this.startMatrix = mat4.clone(startMatrix);
        this.registrationId = registrationId;
    }

    public computeDelta(currentPatient: ReadonlyVec3): ReadonlyVec3 {
        return vec3.subtract(vec3.create(), currentPatient, this.startPatient);
    }
}

export class TranslationAction extends ToolAction {
    private readonly mouseButton: number;

    private _startState?: TranslationActionEventState;
    private _didMove = false;
    private readonly _undoService: RegistrationUndoService;

    public get isActive(): boolean {
        return this._startState !== undefined;
    }

    constructor(mouseButton: number, undoService: RegistrationUndoService) {
        super();
        this.mouseButton = mouseButton;
        this._undoService = undoService;
    }

    public onMousePress(
        button: number,
        _modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: TextureView,
        env?: Environment
    ): EventHandlerResult {
        if (
            button !== this.mouseButton ||
            !(view instanceof PlaneView) ||
            !env?.registration ||
            env.registration.isApproved
        ) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        const startPatient = view.clientToPatientCoordinates(coordinates);
        this._startState = new TranslationActionEventState(
            startPatient,
            env.registration.transformation,
            env.registration.sopInstanceUid
        );
        this._didMove = false;

        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            true
        );
    }

    public onMouseMove(
        coordinates: ReadonlyVec2,
        _buttons: number,
        view: TextureView,
        env?: Environment
    ): EventHandlerResult {
        if (
            !this._startState ||
            !(view instanceof PlaneView) ||
            !env?.registration
        ) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        const currentPatient = view.clientToPatientCoordinates(coordinates);
        const delta = this._startState.computeDelta(currentPatient);

        const newMatrix = mat4.clone(this._startState.startMatrix);
        mat4.translate(newMatrix, newMatrix, delta);

        env.setRegistrationMatrix(newMatrix);
        this._didMove = true;

        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    public onMouseRelease(
        _button: number,
        _coordinates: ReadonlyVec2,
        _view?: TextureView,
        env?: Environment
    ): EventHandlerResult {
        if (this._startState && this._didMove) {
            this._undoService.pushState(
                this._startState.registrationId,
                this._startState.startMatrix
            );
            env?.registration?.syncTransformationToDataset();
        }
        this._startState = undefined;
        this._didMove = false;
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    /**
     * Handles Ctrl/Cmd + Arrow key presses for micro-translation
     * adjustments.
     */
    public onKeyPress(
        key: string,
        modifierKeys: ModifierKeys,
        view: TextureView,
        env?: Environment
    ): EventHandlerResult {
        if (
            !modifierKeys.hasCtrl ||
            !(view instanceof PlaneView) ||
            !env?.registration ||
            env.registration.isApproved
        ) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        let direction: ReadonlyVec3 | null = null;
        if (key === 'ArrowRight') {
            direction = view.viewXDirection;
        } else if (key === 'ArrowLeft') {
            direction = vec3.negate(vec3.create(), view.viewXDirection);
        } else if (key === 'ArrowUp') {
            direction = view.viewYDirection;
        } else if (key === 'ArrowDown') {
            direction = vec3.negate(vec3.create(), view.viewYDirection);
        }

        if (!direction) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        const delta = vec3.scale(vec3.create(), direction, TRANSLATION_STEP_MM);
        const previousMatrix = mat4.clone(env.registration.transformation);
        const newMatrix = mat4.clone(previousMatrix);
        mat4.translate(newMatrix, newMatrix, delta);

        env.setRegistrationMatrix(newMatrix);

        this._undoService.pushState(
            env.registration.sopInstanceUid,
            previousMatrix
        );
        env.registration.syncTransformationToDataset();

        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    public cancelAction(env?: Environment): void {
        if (this._startState && env?.registration) {
            env.setRegistrationMatrix(mat4.clone(this._startState.startMatrix));
        }
        this._startState = undefined;
        this._didMove = false;
    }
}
